<template>
  <div class="pie-graph-table" :class="{ small }">
    <div class="pie-graph-total-container">
      <PieGraph :id="id" :items="filteredItems" @highlight="highlight($event)" />
      <div v-if="totalString" class="total-string">
        <span>{{ totalString }}</span>
      </div>
    </div>
    <PieTable :id="id" :items="filteredItems" :highlighted-row="highlightedRow" />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'
import PieGraph from '@/components/public/GraphTable/PieGraph.vue'
import PieTable from '@/components/public/GraphTable/PieTable.vue'
import { GraphItem } from '@/components/public/GraphTable/types'
import { iterateShades } from '@/helpers/shadeIterator'

const components = {
  PieGraph,
  PieTable,
}
const props = {
  items: {
    type: Array as PropType<GraphItem[]>,
    default: () => [
      { title: 'Line1', value: 100, color: 'red' },
      { title: 'Line2', value: 50, color: 'blue' },
      { title: 'Line3', value: 25, color: 'green' },
    ],
  },
  id: {
    type: String,
    required: true,
  },
  totalString: {
    type: String,
    required: false,
  },
  storyColour: {
    type: String,
    required: false,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<PieGraphTable>({
  components,
  watch: {
    widgetsContainerWidth(width: Number) {
      this.handleWidgetsContainerResize(width)
    },
  },
})
export default class PieGraphTable extends Vue.extend({ props }) {
  highlightedRow: string | null = null
  small = false

  handleWidgetsContainerResize(widgetsContainerWidth: Number) {
    const clientWidth = this.$el ? this.$el.clientWidth : 0
    if (clientWidth > widgetsContainerWidth) {
      this.small = true
    }
  }

  get widgetsContainerWidth() {
    return this.$typedStore.public.size.sidebarWidgetsContainerWidth
  }

  get filteredItems() {
    const items = [...this.items]

    if (this.useStoryColour && this.storyColour) {
      const shades = iterateShades(this.storyColour, items.length)
      items.forEach((item) => {
        item.color = shades.next()
      })
    }

    return items.filter((item) => !this.hidePiechartOnZeroNegative || item.value > 0)
  }

  get appConfig() {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.appConfig
  }

  get hidePiechartOnZeroNegative() {
    return this.appConfig.hidePiechartOnZeroNegative
  }

  get useStoryColour() {
    return this.appConfig.usePieChartStoryColour
  }

  highlight(row: string | null) {
    this.highlightedRow = row
    this.$root.$emit('bv::refresh::table', this.id)
  }
}
</script>

<style lang="scss">
.pie-graph-table {
  padding: 8px 0;
  color: #455 !important;
  font-size: 12px;
  display: flex;
  padding: 8px 16px 8px 4px;
  &.small {
    flex-direction: column;
    padding: 8px 0 16px 0;
  }
  align-items: center;
  justify-content: center;
  gap: 1em;
  .pie-graph-total-container {
    position: relative;
    width: 200px;
    .total-string {
      pointer-events: none;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 16px;
    }
  }
}
</style>
